import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, Page, Tabs, BlockStack, Text } from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';
import './App.css';
import './styles/common.css';

// Auth components
import { AuthProvider } from './contexts/AuthContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { AdminRoute } from './components/common/AdminRoute';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { WorkspacePage } from './pages/WorkspacePage';
import { AdminPage } from './pages/AdminPage';

// Tab components
import { ConfigurationTab } from './components/tabs/ConfigurationTab';
import { EntityTab } from './components/tabs/EntityTab';
import { AppHeader } from './components/common/AppHeader';
// import { useAuth } from './contexts/AuthContext';
import { useWorkspace } from './contexts/WorkspaceContext';
import { configApi } from './services/api/configApi';
import { formatServiceLabel } from './utils/serviceLabels';

// Schedule modal removed (unused)

//
// 3) MAIN APP
//
function App() {
  // track selected tab by id to avoid index mismatch flicker
  const [selectedTabId, setSelectedTabId] = useState('configurations');

  // Minimal services scaffold (UI reads live data within subcomponents)
  const [services, setServices] = useState(() => ({
    products: { processId: null },
    orders: { processId: null },
    inventory: { processId: null },
    prices: { processId: null },
    images: { processId: null },
    fulfillments: { processId: null },
    configurations: { processId: null }
  }));

  // Details table is not used here; subcomponents fetch what they need
  const [details] = useState({});

  // Toggle active/inactive
  const handleToggleActive = (serviceName) => {
    setServices((prev) => {
      const newActive = !prev[serviceName].active;
      // Example API call: https://apps.allurecommerce.com/{{app_id}}/${serviceName}/${on|off}
      return {
        ...prev,
        [serviceName]: {
          ...prev[serviceName],
          active: newActive,
        },
      };
    });
  };

  // Schedule save
  const handleScheduleSave = (serviceName, payload) => {
    // Example API call: https://apps.allurecommerce.com/{{app_id}}/${serviceName}/schedule
  };

  // Sync Now
  const handleSyncNow = async (serviceName) => {
    // Immediately set status to a starting state with a spinner
    setServices((prev) => ({
      ...prev,
      [serviceName]: {
        ...prev[serviceName],
        status: 'Starting',
      },
    }));

    // Example: POST to https://apps.allurecommerce.com/{{app_id}}/${serviceName}/sync
    // Simulate an API call returning a processId
    const fakeProcessId = Math.floor(Math.random() * 100000).toString();

    // In a real call, you'd do something like:
    // const response = await fetch(`https://apps.allurecommerce.com/14024/${serviceName}/sync`, { method: 'POST' });
    // const data = await response.json();
    // const fakeProcessId = data.processId;

    setServices((prev) => ({
      ...prev,
      [serviceName]: {
        ...prev[serviceName],
        processId: fakeProcessId,
      },
    }));

    // Poll every 10 seconds until the status is not Starting or Running
    pollServiceStatus(serviceName, fakeProcessId);
  };

  // Poll for updated status
  const pollServiceStatus = (serviceName, processId) => {
    const pollInterval = setInterval(async () => {
      // Example API call: GET https://apps.allurecommerce.com/{{app_id}}/status/${processId}
      // We'll simulate a random result for demonstration:
      const randomStatus = Math.random() < 0.5 ? 'Success' : 'Error';
      // const response = await fetch(...);
      // const data = await response.json();
      // const randomStatus = data.status; // e.g. 'Working' | 'Success' | 'Error'

      setServices((prev) => {
        const currentStatus = prev[serviceName].status;
        // If it's still in Starting/Running, we might update. Otherwise, do nothing.
        if (currentStatus === 'Starting' || currentStatus === 'Running') {
          // Keep polling during starting/running
          if (randomStatus === 'Starting' || randomStatus === 'Running') {
            return {
              ...prev,
              [serviceName]: {
                ...prev[serviceName],
                status: 'Running',
              },
            };
          } else {
            // If randomStatus is 'Success' or 'Error', we finalize the status and clear the interval
            clearInterval(pollInterval);
            return {
              ...prev,
              [serviceName]: {
                ...prev[serviceName],
                status: randomStatus,
                processId: null,
              },
            };
          }
        }
        return prev; // If it's already success/error, do nothing
      });
    }, 10000);
  };

  // Manual Sync
  const handleManualSync = (serviceName, commaSeparatedIds) => {
    if (!commaSeparatedIds.trim()) return;
    // Example API call: https://apps.allurecommerce.com/{{app_id}}/${serviceName}/${commaSeparatedIds}
    // In real code:
    // fetch(`https://apps.allurecommerce.com/14024/${serviceName}/${commaSeparatedIds}`, { method: 'POST' })
    //   .then(...)
    //   .catch(...);
  };

  return (
    <Router>
      <AuthProvider>
        <WorkspaceProvider>
          <AppProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              
              {/* Workspace selection */}
              <Route path="/workspace" element={
                <ProtectedRoute>
                  <WorkspacePage />
                </ProtectedRoute>
              } />
              
              {/* Admin panel */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              } />
              
              {/* Main app route */}
              <Route path="/app" element={
                <ProtectedRoute>
                  <MainApp 
                    selectedTabId={selectedTabId}
                    setSelectedTabId={setSelectedTabId}
                    services={services}
                    details={details}
                    handleToggleActive={handleToggleActive}
                    handleScheduleSave={handleScheduleSave}
                    handleSyncNow={handleSyncNow}
                    handleManualSync={handleManualSync}
                  />
                </ProtectedRoute>
              } />
              
              {/* Redirect root to login */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </AppProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </Router>
  );
}

// MainApp component for the protected main application
function MainApp({ 
  selectedTabId, 
  setSelectedTabId, 
  services, 
  details, 
  handleToggleActive, 
  handleScheduleSave, 
  handleSyncNow, 
  handleManualSync 
}) {
  const { isTenantContextReady } = useWorkspace();
  const [tabs, setTabs] = useState([{ id: 'configurations', content: 'Configurations' }]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [initializedDefaultTab, setInitializedDefaultTab] = useState(false);

  useEffect(() => {
    if (!isTenantContextReady) return;
    setLoadingTabs(true);
    configApi.getServices()
      .then((resp) => {
        if (resp?.success) {
          const preferredServiceOrder = [
            'qbo_customer_deposits',
            'qbo_shipment_invoices',
            'qbo_refunds',
            'qbo_vendor_bills'
          ];
          const orderIndex = (key) => {
            const index = preferredServiceOrder.indexOf(key);
            return index === -1 ? Number.MAX_SAFE_INTEGER : index;
          };
          const svcTabs = (resp.data || [])
            .filter((s) => s?.allocated !== false && s?.ui?.showTab !== false) // require allocation and UI visible
            .sort((a, b) => orderIndex(a.key) - orderIndex(b.key))
            .map((s) => {
              const label = formatServiceLabel(s.key, s.label);
              return { id: s.key, content: label, ui: s.ui, label, mode: s.mode };
            });
          // Append Configurations tab at end
          setTabs([...svcTabs, { id: 'configurations', content: 'Configurations' }]);
          // On first load, if there is at least one service tab, make it the default selection
          if (!initializedDefaultTab) {
            const firstServiceId = svcTabs[0]?.id;
            if (firstServiceId) {
              setSelectedTabId(firstServiceId);
            } else {
              setSelectedTabId('configurations');
            }
            setInitializedDefaultTab(true);
          }
        } else {
          setTabs([{ id: 'configurations', content: 'Configurations' }]);
        }
      })
      .catch(() => setTabs([{ id: 'configurations', content: 'Configurations' }]))
      .finally(() => setLoadingTabs(false));
  }, [isTenantContextReady]);

  if (loadingTabs) {
    return (
      <Page>
        <AppHeader />
        <BlockStack gap="tight">
          <Text>Loading services...</Text>
        </BlockStack>
      </Page>
    );
  }

  const selectedIndex = Math.max(0, tabs.findIndex(t => t.id === selectedTabId));
  const currentTab = tabs[selectedIndex]?.id || 'configurations';
  const svcForTab = (() => {
    const tab = tabs[selectedIndex];
    if (!tab || tab.id === 'configurations') return null;
    return {
      key: tab.id,
      ui: tab.ui,
      label: tab.label || tab.content,
      mode: tab.mode || 'schedule'
    };
  })();

  const handleTabSelect = (index) => {
    const id = tabs[index]?.id || 'configurations';
    setSelectedTabId(id);
  };

  return (
    <Page>
      <AppHeader />
      <Tabs
        tabs={tabs}
        selected={selectedIndex}
        onSelect={handleTabSelect}
      />
      {currentTab === 'configurations' ? (
        <ConfigurationTab />
      ) : (
        <EntityTab service={svcForTab?.key} ui={svcForTab?.ui} label={svcForTab?.label} mode={svcForTab?.mode} />
      )}
    </Page>
  );
}

export default App;
